import { useState } from 'react';
import type { RatesSettings } from '@/settings/types';

export type InputMode = 'bill' | 'units';

export interface CalculatorInputs {
  totalFans: number;
  mode: InputMode;
  billLkr: number | null;
  units: number | null;
  hoursPerDay: number;
}

const INPUT_CLS =
  'w-full rounded-md border border-slate-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-500';

export function CalculatorForm({
  rates,
  onChange,
}: {
  rates: RatesSettings;
  onChange: (inputs: CalculatorInputs | null) => void;
}) {
  const [totalFans, setTotalFans] = useState<string>('2');
  const [mode, setMode] = useState<InputMode>('units');
  const [billLkr, setBillLkr] = useState<string>('');
  const [units, setUnits] = useState<string>('130');

  const push = (next: {
    totalFans?: string;
    mode?: InputMode;
    billLkr?: string;
    units?: string;
  }) => {
    const f = next.totalFans ?? totalFans;
    const m = next.mode ?? mode;
    const b = next.billLkr ?? billLkr;
    const u = next.units ?? units;
    const fansN = Number(f);
    const billN = Number(b);
    const unitsN = Number(u);
    const hrsN = rates.fans.defaultHoursPerDay;
    if (!Number.isFinite(fansN) || fansN <= 0) return onChange(null);
    if (m === 'bill') {
      if (!Number.isFinite(billN) || billN <= 0) return onChange(null);
      onChange({ totalFans: fansN, mode: 'bill', billLkr: billN, units: null, hoursPerDay: hrsN });
    } else {
      if (!Number.isFinite(unitsN) || unitsN <= 0) return onChange(null);
      onChange({ totalFans: fansN, mode: 'units', billLkr: null, units: unitsN, hoursPerDay: hrsN });
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 sm:p-6 space-y-4 sm:space-y-5">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          How many fans are in your house?
        </label>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={totalFans}
          onChange={(e) => {
            setTotalFans(e.target.value);
            push({ totalFans: e.target.value });
          }}
          className={INPUT_CLS}
        />
      </div>

      <div>
        <div className="text-sm font-medium text-slate-700 mb-2">Your monthly electricity consumption</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button
            type="button"
            onClick={() => {
              setMode('units');
              push({ mode: 'units' });
            }}
            className={`px-3 py-2.5 rounded-md text-sm font-medium ${
              mode === 'units'
                ? 'bg-brand-500 text-slate-900'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Units (kWh)
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('bill');
              push({ mode: 'bill' });
            }}
            className={`px-3 py-2.5 rounded-md text-sm font-medium ${
              mode === 'bill'
                ? 'bg-brand-500 text-slate-900'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Bill (Rs.)
          </button>
        </div>

        {mode === 'units' ? (
          <div>
            <label className="block text-xs text-slate-500 mb-1">Monthly units (kWh)</label>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.1}
              value={units}
              onChange={(e) => {
                setUnits(e.target.value);
                push({ units: e.target.value });
              }}
              className={INPUT_CLS}
              placeholder="e.g. 200"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Monthly bill total from CEB (Rs., including SSCL)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              value={billLkr}
              onChange={(e) => {
                setBillLkr(e.target.value);
                push({ billLkr: e.target.value });
              }}
              className={INPUT_CLS}
              placeholder="e.g. 10615"
            />
          </div>
        )}
      </div>
    </div>
  );
}
